import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { MailCheck, Eye, Send } from "lucide-react";

export const MonthlyDigestCard = () => {
  const [enabled, setEnabled] = useState(true);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/notifications/preferences"); setEnabled(data.monthly_digest !== false); }
      catch (e) { console.error("Failed to load digest preference:", e); }
    })();
  }, []);

  const toggle = async (v) => {
    setEnabled(v);
    try {
      await api.patch("/notifications/preferences", { monthly_digest: v });
      toast.success(v ? "Monthly digest on" : "Monthly digest off");
    } catch (e) { toast.error("Could not save that preference"); setEnabled(!v); }
  };

  const showPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/digest/preview");
      setPreview(data); setOpen(true);
    } catch (e) { toast.error("Could not build your digest"); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/send");
      toast.success("Digest sent", { description: data.subject });
      setOpen(false);
    } catch (e) { toast.error("Could not send the digest"); }
    finally { setBusy(false); }
  };

  const d = preview?.digest;

  return (
    <Card className="p-5 shadow-premium" data-testid="monthly-digest-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-sm font-medium"><MailCheck className="h-4 w-4" />Monthly digest</div>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="digest-toggle" />
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">
        A short email at the start of each month: what you put in, what it earned and your best performer.
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" className="gap-1.5" onClick={showPreview} disabled={busy}
          data-testid="digest-preview-button">
          <Eye className="h-3.5 w-3.5" />Preview
        </Button>
        <Button size="sm" variant="ghost" className="gap-1.5" onClick={sendNow} disabled={busy}
          data-testid="digest-send-button">
          <Send className="h-3.5 w-3.5" />Email it to me
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle>{preview?.subject || "Your monthly summary"}</DialogTitle>
            <DialogDescription>This is exactly what lands in your inbox each month.</DialogDescription>
          </DialogHeader>
          {d && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[["Money in", d.money_in_usd], ["Auto-invested", d.auto_invested_usd],
                  ["Net worth", d.net_worth_usd], ["Unrealised gain", d.gain_usd]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-secondary/60 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                    <div className="numeric text-lg font-semibold">{fmtUSD(v)}</div>
                  </div>
                ))}
              </div>
              {d.best && (
                <div className="text-sm" data-testid="digest-best">
                  Best performer: <span className="font-medium">{d.best.symbol}</span>{" "}
                  <span className={d.best.pnl_pct >= 0 ? "text-emerald-600" : "text-red-600"}>
                    {d.best.pnl_pct >= 0 ? "+" : ""}{d.best.pnl_pct}%
                  </span>
                </div>
              )}
              <pre className="rounded-lg border bg-secondary/40 p-3 text-[11px] leading-relaxed whitespace-pre-wrap max-h-64 overflow-auto"
                data-testid="digest-preview-body">{preview.body}</pre>
              <Button className="w-full gap-1.5" onClick={sendNow} disabled={busy} data-testid="digest-dialog-send">
                <Send className="h-4 w-4" />Send this to my inbox
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
